"use client";
import { BMPrimitives, BMText, useBMTheme } from "@traveloka/district-bloom";
import React from "react";
import { StyleSheet } from "react-native";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import BMView from "../../View/BMView";

export type ChartTooltipProps = TooltipProps<number, string>;

export type LineSeries = {
  key: string;
  color: string;
  label: string;
};

type Props = {
  data: Record<string, string | number>[];
  series: LineSeries[];
  note?: string;
  yAxisWidth?: number;
  yAxisTicks?: number[];
  yAxisDomain?: [number | string, number | string];
  xAxisInterval?: number;
  formatYAxisTick?: (v: string | number) => string;
  formatXAxisTick?: (v: string | number) => string;
  renderTooltip?: (props: ChartTooltipProps) => React.ReactElement | null;
};

export default function AnalyticsLineChart({
  data,
  series,
  note,
  yAxisTicks,
  yAxisDomain,
  yAxisWidth = 25,
  xAxisInterval,
  formatYAxisTick,
  formatXAxisTick,
  renderTooltip,
}: Props) {
  const { BMComponents } = useBMTheme();

  return (
    <BMView flex style={styles.container}>
      <BMView flex style={styles.chartArea}>
        <ResponsiveContainer width="100%" minHeight={200}>
          <LineChart
            data={data}
            margin={{
              top: BMPrimitives.BMSize.xs,
              right: BMPrimitives.BMSize.xl2,
              bottom: BMPrimitives.BMSize.xs,
              left: BMPrimitives.BMSize.lg,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={BMComponents.BMStyleLineBordercolorSubtle}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={<AxisTick formatter={formatXAxisTick} />}
              axisLine={false}
              tickLine={false}
              interval={xAxisInterval}
            />
            <YAxis
              tick={<AxisTick formatter={formatYAxisTick} />}
              axisLine={false}
              tickLine={false}
              ticks={yAxisTicks}
              width={yAxisWidth}
              domain={yAxisDomain}
            />
            {renderTooltip ? <Tooltip content={renderTooltip} /> : null}
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </BMView>
      <BMView row justify="between" align="center" style={styles.footer}>
        {note ? (
          <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
            {note}
          </BMText>
        ) : null}
        <BMView row spacing="lg" style={styles.legend}>
          {series.map((s) => (
            <BMView key={s.key} row align="center" spacing="xs">
              <BMView style={[styles.dot, { backgroundColor: s.color }]} />
              <BMText variant="uiSmallLi" style={{ color: s.color }}>
                {s.label}
              </BMText>
            </BMView>
          ))}
        </BMView>
      </BMView>
    </BMView>
  );
}

type AxisTickProps = {
  x: number;
  y: number;
  payload: { value: string | number };
  textAnchor?: "middle" | "start" | "end" | "inherit";
  formatter?: (v: string | number) => string;
};

function isAxisTickProps(
  props: Record<string, unknown>
): props is AxisTickProps {
  const payload = props.payload;
  if (typeof props.x !== "number") return false;
  if (typeof props.y !== "number") return false;
  if (typeof payload !== "object" || payload === null) return false;
  return "value" in payload;
}

function AxisTick(props: Record<string, unknown>) {
  const { BMComponents } = useBMTheme();
  if (!isAxisTickProps(props)) return null;
  const { x, y, payload, textAnchor, formatter } = props;
  const label = formatter ? formatter(payload.value) : String(payload.value);
  if (!label) return null;
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor={textAnchor ?? "middle"}
      fill={BMComponents.BMStyleTextInkMuted}
      fontSize={10}
    >
      {label}
    </text>
  );
}

const styles = StyleSheet.create({
  container: {
    minWidth: 0,
  },
  chartArea: {
    minHeight: 0,
  },
  footer: {
    marginTop: BMPrimitives.BMSize.sm,
    paddingHorizontal: BMPrimitives.BMSize.md,
  },
  legend: {
    flexShrink: 1,
    minWidth: 0,
    flexWrap: "wrap",
    rowGap: BMPrimitives.BMSize.xs,
  },
  dot: {
    width: BMPrimitives.BMSize.sm,
    height: BMPrimitives.BMSize.sm,
    borderRadius: BMPrimitives.BMRadius.rounded,
  },
});
